<template>
  <ion-page>
    <ion-content class="home-page" scroll-y="true">
      <div class="main-container">
        
        <!-- Header Profile & Logo -->
        <div class="header-section">
          <div class="logo-area">
            <img src="/assets/icon/logo_desa.svg" alt="Logo" class="logo-img" />
            <div class="header-text">
              <h2 class="title">Sistem Presensi</h2>
              <p class="subtitle">Desa Cinto Mandi</p>
            </div>
          </div>
        </div>

        <!-- Welcome & Scan Card -->
        <div class="welcome-card">
          <div class="welcome-header">
            <div>
              <p class="date-text">{{ ConvertFullDate(new Date()) }}</p>
              <h1 class="greeting">Halo, {{ userName || 'Petugas' }}</h1>
              <div class="badges-container">
                <p class="role-badge">{{ role }}</p>
                <p class="status-badge" :class="userStatus === 'aktif' ? 'bg-success' : 'bg-warning'">
                  {{ userStatus === 'aktif' ? 'Aktif' : 'Non-Aktif' }}
                </p>
              </div>
            </div>
          </div>
          
          <div class="scan-area">
            <ion-button
              class="btn-scan"
              expand="block"
              @click="scanAndAttend"
              v-if="!isWebScanning"
            >
              <ion-icon slot="start" :icon="qrCodeOutline"></ion-icon>
              Scan QR Absen
            </ion-button>
            
            <ion-button
              class="btn-scan"
              color="danger"
              expand="block"
              @click="stopWebScan"
              v-if="isWebScanning"
            >
              <ion-icon slot="start" :icon="qrCodeOutline"></ion-icon>
              Batalkan Scan
            </ion-button>
            <div id="reader" v-show="isWebScanning" style="width: 100%; margin-top: 16px; border-radius: 12px; overflow: hidden; background: white;"></div>

            <p class="status-info" v-if="!isAbsenMasuk">Anda sudah absen masuk hari ini</p>
          </div>
        </div>

        <!-- Quick Actions -->
        <div class="quick-actions">
          <h3 class="section-title">Menu Cepat</h3>
          <ion-grid>
            <ion-row>
              <ion-col size="6">
                <div class="action-card" @click="toNavigate('laporankehadiranPetugas')">
                  <div class="icon-wrapper bg-blue">
                    <ion-icon :icon="listOutline"></ion-icon>
                  </div>
                  <p>Data Absensi</p>
                </div>
              </ion-col>
              <ion-col size="6">
                <div class="action-card" @click="logOut()">
                  <div class="icon-wrapper bg-red">
                    <ion-icon :icon="logOutOutline"></ion-icon>
                  </div>
                  <p>Logout</p>
                </div>
              </ion-col>
            </ion-row>
          </ion-grid>
        </div>

        <!-- Weekly Report List (Mobile Friendly) -->
        <div class="report-section">
          <h3 class="section-title">Laporan Absen Mingguan</h3>
          
          <div class="empty-state" v-if="attendanceRecords.length === 0">
            <p>Belum ada data absensi minggu ini.</p>
          </div>

          <ion-list class="custom-list" v-else>
            <ion-item v-for="record in attendanceRecords.slice(0, 7)" :key="record.date" class="report-item" lines="none">
              <div class="record-card">
                <div class="record-header">
                  <h4>{{ formatDayDate(record.date) }}</h4>
                  <ion-badge :color="record.masuk ? (isLate(record.masuk) ? 'danger' : 'success') : 'warning'">
                    {{ record.masuk ? (isLate(record.masuk) ? 'Terlambat' : 'Tepat') : '-' }}
                  </ion-badge>
                </div>
                
                <div class="record-details">
                  <div class="detail-box">
                    <span class="label">Masuk</span>
                    <span class="time" :class="{'text-danger': record.masuk && isLate(record.masuk)}">
                      {{ record && record.masuk ? formatTime(record.masuk) : '--:--' }}
                    </span>
                  </div>
                  <div class="detail-box">
                    <span class="label">Keluar</span>
                    <span class="time">
                      {{ record && record.keluar ? formatTime(record.keluar) : '--:--' }}
                    </span>
                  </div>
                </div>
              </div>
            </ion-item>
          </ion-list>
        </div>

      </div>

      <ion-toast
        :is-open="toast.isOpen"
        :message="toast.message"
        :duration="toast.duration"
        :color="toast.color"
        @did-dismiss="toast.isOpen = false"
      ></ion-toast>

      <ion-loading
        :is-open="isLoading"
        :message="loadingMessage"
        spinner="crescent"
      ></ion-loading>
    </ion-content>
  </ion-page>
</template>

<script setup>
import { ref, onMounted } from "vue";
import {
  IonPage,
  IonContent,
  IonGrid,
  IonRow,
  IonCol,
  IonButton,
  IonList,
  IonItem,
  IonToast,
  IonBadge,
  IonIcon,
  IonLoading,
  alertController,
} from "@ionic/vue";
import { qrCodeOutline, listOutline, logOutOutline } from "ionicons/icons";
import { ConvertFullDate } from "../composables/composables";
import { useRouter } from "vue-router";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  collection,
  query,
  getDocs,
} from "firebase/firestore";
import { firebaseApp } from "../views/firebase.js";
import { BarcodeScanner } from "@capacitor-community/barcode-scanner";
import { Capacitor } from "@capacitor/core";
import { Html5Qrcode } from "html5-qrcode";

const router = useRouter();

const userName = ref("");
const role = ref("");
const userStatus = ref("");
const isAbsenMasuk = ref(true);
const absenLabel = ref("Absen Masuk");
const isWebScanning = ref(false);
let html5QrCode = null;
const attendanceRecords = ref([]);
const weeklyAttendanceRecords = ref([]);
const isLoading = ref(false);
const loadingMessage = ref("Memproses...");
const isProcessingScan = ref(false);
const toast = ref({
  isOpen: false,
  message: "",
  duration: 2000,
  color: "success",
});

const errorMessage = ref("");

const allowedCoordinates = ref(null);

const auth = getAuth(firebaseApp);
const db = getFirestore(firebaseApp);



const fetchUserName = async (uid) => {
  try {
    const userDoc = await getDoc(doc(db, "users", uid));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      userName.value = userData.fullName;
      role.value = userData.role;
      userStatus.value = userData.status;
    }
  } catch (error) {
    console.error("Error fetching user data:", error);
  }
};

const fetchCoordinates = async () => {
  try {
    const locationDoc = await getDoc(doc(db, "settings", "coordinates"));
    if (locationDoc.exists()) {
      allowedCoordinates.value = locationDoc.data();
    }
  } catch (error) {
    console.error("Error fetching coordinates:", error);
  }
};

const fetchAttendanceRecords = async (uid) => {
  const recordsRef = collection(db, "attendance", uid, "records");
  const q = query(recordsRef);
  try {
    const querySnapshot = await getDocs(q);
    let records = querySnapshot.docs.map((doc) => ({
      date: doc.id,
      ...doc.data(),
    }));
    
    // Urutkan berdasarkan tanggal terbaru di atas
    records.sort((a, b) => new Date(b.date) - new Date(a.date));
    
    attendanceRecords.value = records;
    filterWeeklyRecords();
  } catch (error) {
    console.error("Error fetching attendance records:", error);
    errorMessage.value = error.message || error.toString();
  }
};

const filterWeeklyRecords = () => {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1));
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);

  weeklyAttendanceRecords.value = [];

  for (
    let date = new Date(startOfWeek);
    date <= now;
    date.setDate(date.getDate() + 1)
  ) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;
    const record = attendanceRecords.value.find(
      (record) => record.date === formattedDate
    );
    weeklyAttendanceRecords.value.push(
      record || { date: formattedDate, masuk: null, keluar: null }
    );
  }
};

const checkAbsenStatus = async (uid) => {
  const today = getLocalDateString();
  const recordRef = doc(db, "attendance", uid, "records", today);
  try {
    const docSnapshot = await getDoc(recordRef);
    if (docSnapshot.exists()) {
      if (docSnapshot.data().keluar) {
        isAbsenMasuk.value = true;
        absenLabel.value = "Absen Masuk";
      } else {
        isAbsenMasuk.value = false;
        absenLabel.value = "Absen Keluar";
      }
    } else {
      isAbsenMasuk.value = true;
      absenLabel.value = "Absen Masuk";
    }
  } catch (error) {
    console.error("Error checking absen status:", error);
  }
};

const getLocalDateString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const scanAndAttend = async () => {
  if (Capacitor.isNativePlatform()) {
    try {
      const status = await BarcodeScanner.checkPermission({ force: true });
      if (!status.granted) {
        showToast("Izin kamera tidak diberikan.", "danger");
        return;
      }

      document.querySelector("body").classList.add("scanner-active");
      BarcodeScanner.hideBackground();
      const result = await BarcodeScanner.startScan();

      BarcodeScanner.showBackground();
      document.querySelector("body").classList.remove("scanner-active");

      if (result.hasContent) {
        if (result.content === "SECRET_QR_CODE_DESA_CINTO_MANDI") {
          showToast("QR Code valid. Memproses absensi...", "success");
          await handleAbsen();
        } else {
          showToast("QR Code tidak valid.", "danger");
        }
      } else {
        showToast("Tidak ada QR Code yang terdeteksi.", "warning");
      }
    } catch (error) {
      console.error("Error during scanning:", error);
      showToast("Terjadi kesalahan saat memindai.", "danger");
      BarcodeScanner.showBackground();
      document.querySelector("body").classList.remove("scanner-active");
    }
  } else {
    isWebScanning.value = true;
    setTimeout(() => {
      html5QrCode = new Html5Qrcode("reader");
      const config = { fps: 10, qrbox: { width: 250, height: 250 } };
      
      html5QrCode.start(
        { facingMode: "environment" }, 
        config,
        async (decodedText) => {
          if (isProcessingScan.value) return; // Mencegah scan berulang
          
          let isBarcodeValid = false;
          let mockLat = null;
          let mockLng = null;
          
          // Coba parse JSON dari barcode baru (Skripsi Bypass)
          try {
            const data = JSON.parse(decodedText);
            if (data && data.type === "attendance") {
              isBarcodeValid = true;
              mockLat = data.lat;
              mockLng = data.lng;
            }
          } catch(e) {
            // Fallback ke barcode rahasia lama
            if (decodedText === "SECRET_QR_CODE_DESA_CINTO_MANDI") {
              isBarcodeValid = true;
            }
          }
          
          if (isBarcodeValid) {
            isProcessingScan.value = true;
            
            // Set global variabel untuk bypass GPS jika ada dari QR Code
            if (mockLat !== null && mockLng !== null) {
              window.mockGpsLat = mockLat;
              window.mockGpsLng = mockLng;
            } else {
              window.mockGpsLat = undefined;
              window.mockGpsLng = undefined;
            }
            
            stopWebScan();
            showToast("QR Code valid. Memproses absensi...", "success");
            await handleAbsen();
            isProcessingScan.value = false;
          } else {
            stopWebScan();
            showToast("QR Code tidak valid.", "danger");
          }
        },
        () => {
          // parse error, ignore
        }
      ).catch((err) => {
        isWebScanning.value = false;
        console.error(err);
        showToast("Gagal mengakses kamera web. Pastikan izin kamera aktif.", "danger");
      });
    }, 200);
  }
};

const stopWebScan = () => {
  if (html5QrCode) {
    html5QrCode.stop().then(() => {
      html5QrCode.clear();
      html5QrCode = null;
      isWebScanning.value = false;
    }).catch((err) => {
      console.error("Failed to stop scanning", err);
      html5QrCode = null;
      isWebScanning.value = false;
    });
  } else {
    isWebScanning.value = false;
  }
};

const absenMasuk = async () => {
  const user = auth.currentUser;
  if (user) {
    if (userStatus.value === "non-aktif") {
      showToast("Status Anda non-aktif. Anda tidak dapat melakukan absensi.", "danger");
      return;
    }
    
    isLoading.value = true;
    loadingMessage.value = "Mengambil koordinat GPS Anda...";
    
    const locationResult = await checkGeolocation();
    if (!locationResult.success) {
      isLoading.value = false;
      if (locationResult.error) {
        const alert = await alertController.create({
          header: 'GPS Tidak Aktif / Ditolak',
          message: locationResult.error + '\nPastikan izin lokasi di browser/perangkat Anda sudah aktif!',
          buttons: ['OK']
        });
        await alert.present();
      } else {
        const msg = `Jarak Anda: ${locationResult.distance} km dari pusat.\nAbsen ditolak oleh Algoritma Haversine.`;
        const alert = await alertController.create({
          header: 'Titik Koordinat Tidak Pas',
          message: msg,
          buttons: ['OK']
        });
        await alert.present();
      }
      return;
    }
    
    loadingMessage.value = "Menyimpan data absensi masuk...";
    const recordRef = doc(db, "attendance", user.uid, "records", getLocalDateString());
    try {
      const docSnapshot = await getDoc(recordRef);
      if (docSnapshot.exists() && docSnapshot.data().masuk) {
        isLoading.value = false;
        showToast("Anda sudah melakukan absensi masuk hari ini.", "danger");
        return;
      }
      await setDoc(recordRef, { masuk: serverTimestamp() }, { merge: true });
      isLoading.value = false;
      showToast("Absen masuk berhasil", "success");
      isAbsenMasuk.value = false;
      absenLabel.value = "Absen Keluar";
      fetchAttendanceRecords(user.uid);
    } catch (error) {
      isLoading.value = false;
      console.error("Error setting masuk time:", error);
    }
  }
};

const absenKeluar = async () => {
  const user = auth.currentUser;
  if (user) {
    if (userStatus.value === "non-aktif") {
      showToast("Status Anda non-aktif. Anda tidak dapat melakukan absensi.", "danger");
      return;
    }
    
    isLoading.value = true;
    loadingMessage.value = "Memverifikasi lokasi untuk absen keluar...";
    
    const locationResult = await checkGeolocation();
    if (!locationResult.success) {
      isLoading.value = false;
      if (locationResult.error) {
        const alert = await alertController.create({
          header: 'GPS Tidak Aktif / Ditolak',
          message: locationResult.error + '\nPastikan izin lokasi di browser/perangkat Anda sudah aktif!',
          buttons: ['OK']
        });
        await alert.present();
      } else {
        const msg = `Jarak Anda: ${locationResult.distance} km dari pusat.\nAbsen ditolak oleh Algoritma Haversine.`;
        const alert = await alertController.create({
          header: 'Titik Koordinat Tidak Pas',
          message: msg,
          buttons: ['OK']
        });
        await alert.present();
      }
      return;
    }
    
    loadingMessage.value = "Menyimpan data absensi keluar...";
    const recordRef = doc(db, "attendance", user.uid, "records", getLocalDateString());
    try {
      const docSnapshot = await getDoc(recordRef);
      if (!docSnapshot.exists() || docSnapshot.data().keluar) {
        isLoading.value = false;
        showToast("Anda belum melakukan absensi masuk atau sudah melakukan absensi keluar hari ini.", "danger");
        return;
      }
      await updateDoc(recordRef, { keluar: serverTimestamp() });
      isLoading.value = false;
      showToast("Absen keluar berhasil", "success");
      isAbsenMasuk.value = true;
      absenLabel.value = "Absen Masuk";
      fetchAttendanceRecords(user.uid);
    } catch (error) {
      isLoading.value = false;
      console.error("Error setting keluar time:", error);
    }
  }
};

const handleAbsen = async () => {
  if (isAbsenMasuk.value) {
    await absenMasuk();
  } else {
    await absenKeluar();
  }
};

const showToast = (message, color) => {
  toast.value = { isOpen: true, message, duration: 2000, color };
};

const checkGeolocation = async () => {
  return new Promise((resolve) => {
    if (!allowedCoordinates.value) {
      resolve({ success: false, error: "Koordinat absensi belum disetting atau belum dimuat. Coba beberapa saat lagi." });
      return;
    }

    // === FITUR BYPASS DARI QR CODE (Skripsi) ===
    if (window.mockGpsLat !== undefined && window.mockGpsLng !== undefined) {
      loadingMessage.value = "Membaca GPS dari QR Code..."; // Update loading text
      setTimeout(() => {
        const distance = calculateDistance(
          window.mockGpsLat,
          window.mockGpsLng,
          allowedCoordinates.value.latitude,
          allowedCoordinates.value.longitude
        );
        const maxDistance = 50.0;
        const isWithin = distance <= maxDistance;
        
        resolve({
          success: isWithin,
          distance: distance.toFixed(2),
          userLat: window.mockGpsLat,
          userLng: window.mockGpsLng
        });
      }, 800); // Simulasi delay
      return;
    }
    // ============================================

    if (navigator.geolocation) {
      loadingMessage.value = "Menunggu izin akses GPS..."; // Update loading text
      navigator.geolocation.getCurrentPosition(
        (position) => {
          loadingMessage.value = "Menghitung algoritma Haversine..."; // Update loading text
          const userLat = position.coords.latitude;
          const userLng = position.coords.longitude;
          const distance = calculateDistance(
            userLat,
            userLng,
            allowedCoordinates.value.latitude,
            allowedCoordinates.value.longitude
          );
          
          // Toleransi jarak diperlebar untuk uji coba (50 km)
          const maxDistance = 50.0; 
          const isWithin = distance <= maxDistance;
          
          resolve({
            success: isWithin,
            distance: distance.toFixed(2), // Format 2 desimal
            userLat,
            userLng
          });
        },
        (error) => {
          console.error("Error getting geolocation:", error);
          // FALLBACK SIMULASI UNTUK DEMO SKRIPSI
          const askLat = window.prompt("GPS Gagal diakses oleh browser!\n\n(FITUR DEMO SKRIPSI)\nMasukkan Latitude simulasi Anda untuk menguji Algoritma Haversine:", allowedCoordinates.value.latitude);
          if (askLat === null) {
            resolve({ success: false, error: "GPS ditolak dan simulasi dibatalkan." });
            return;
          }
          const askLng = window.prompt("Masukkan Longitude simulasi Anda:", allowedCoordinates.value.longitude);
          
          const userLat = parseFloat(askLat);
          const userLng = parseFloat(askLng);
          
          loadingMessage.value = "Menghitung algoritma Haversine (Mode Simulasi)...";
          const distance = calculateDistance(
            userLat,
            userLng,
            allowedCoordinates.value.latitude,
            allowedCoordinates.value.longitude
          );
          
          const maxDistance = 50.0; 
          const isWithin = distance <= maxDistance;
          
          resolve({
            success: isWithin,
            distance: distance.toFixed(2),
            userLat,
            userLng
          });
        },
        { enableHighAccuracy: true, timeout: 5000 } // Set timeout 5 seconds for faster fallback
      );
    } else {
      console.error("Geolocation is not supported by this browser.");
      resolve({ success: false, error: "Browser tidak mendukung GPS" });
    }
  });
};

const calculateDistance = (lat1, lng1, lat2, lng2) => {
  // ALGORITMA HAVERSINE (Untuk Skripsi)
  const toRad = (value) => (value * Math.PI) / 180;
  const R = 6371; // Radius bumi dalam Kilometer
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // Hasil dalam Kilometer
};

const isLate = (timestamp) => {
  if (!timestamp) return false;
  try {
    const time = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const eightAM = new Date(time);
    eightAM.setHours(8, 0, 0, 0);
    return time > eightAM;
  } catch (e) {
    console.error("isLate error:", e);
    return false;
  }
};

const formatTime = (timestamp) => {
  if (!timestamp) return '--:--';
  try {
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  } catch (e) {
    return '--:--';
  }
};

const formatDayDate = (dateString) => {
  const date = new Date(dateString);
  const days = ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"];
  const day = days[date.getDay()];
  const formattedDate = date.toLocaleDateString("id-ID", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  return `${day}, ${formattedDate}`;
};

const logOut = async () => {
  const alert = await alertController.create({
    cssClass: "my-custom-class",
    header: "Konfirmasi",
    mode: "ios",
    message: "Apakah Anda yakin ingin keluar?",
    buttons: [
      { text: "Batal", role: "cancel" },
      {
        text: "Logout",
        handler: async () => {
          try {
            await signOut(auth);
            localStorage.clear();
            sessionStorage.clear();
            router.push({ name: "login" }).then(() => router.go(0));
          } catch (error) {
            console.error("Logout error", error);
          }
        },
      },
    ],
  });
  await alert.present();
};

onMounted(() => {
  onAuthStateChanged(auth, (user) => {
    if (user) {
      fetchUserName(user.uid);
      fetchCoordinates();
      fetchAttendanceRecords(user.uid);
      checkAbsenStatus(user.uid);
    } else {
      router.push({ name: "login" });
    }
  });
});

const toNavigate = (routeName) => {
  router.push({ name: routeName });
};
</script>

<style scoped>
ion-content.home-page {
  --background: #f4f6f9;
}

.main-container {
  padding: 16px;
  padding-bottom: 80px; /* spacing for bottom tabs */
}

/* Header Section */
.header-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.logo-area {
  display: flex;
  align-items: center;
}

.logo-img {
  width: 50px;
  height: 50px;
  margin-right: 12px;
}

.header-text .title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #0d47a1;
  margin: 0 0 2px 0;
}

.header-text .subtitle {
  font-size: 0.9rem;
  color: #666;
  margin: 0;
}

/* Welcome Card */
.welcome-card {
  background: linear-gradient(135deg, #0d47a1, #1976d2);
  border-radius: 20px;
  padding: 24px;
  color: white;
  box-shadow: 0 10px 20px rgba(13, 71, 161, 0.2);
  margin-bottom: 24px;
}

.date-text {
  font-size: 0.85rem;
  opacity: 0.9;
  margin: 0 0 8px 0;
}

.greeting {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 8px 0;
}

.role-badge {
  display: inline-block;
  background: rgba(255, 255, 255, 0.2);
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.85rem;
  margin: 0 8px 0 0;
  text-transform: capitalize;
}

.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.85rem;
  margin: 0;
  text-transform: capitalize;
  font-weight: 600;
}

.bg-success {
  background: #2dd36f;
  color: white;
}

.bg-warning {
  background: #ffc409;
  color: #333;
}

.badges-container {
  display: flex;
  align-items: center;
}

.scan-area {
  margin-top: 24px;
}

.btn-scan {
  --background: #ffffff;
  --color: #0d47a1;
  --background-hover: #f0f0f0;
  --border-radius: 12px;
  font-weight: 700;
  height: 54px;
  margin: 0;
  --box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.status-info {
  text-align: center;
  font-size: 0.85rem;
  margin: 12px 0 0 0;
  opacity: 0.9;
}

/* Quick Actions */
.section-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 16px;
  margin-top: 0;
}

.quick-actions {
  margin-bottom: 24px;
}

.action-card {
  background: white;
  border-radius: 16px;
  padding: 16px;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0,0,0,0.03);
  cursor: pointer;
  transition: transform 0.2s;
}

.action-card:active {
  transform: scale(0.95);
}

.icon-wrapper {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px auto;
  font-size: 1.5rem;
}

.bg-blue {
  background: rgba(13, 71, 161, 0.1);
  color: #0d47a1;
}

.bg-red {
  background: rgba(235, 68, 90, 0.1);
  color: #eb445a;
}

.action-card p {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: #444;
}

/* Weekly Report */
.report-section {
  margin-bottom: 20px;
}

.custom-list {
  background: transparent;
  padding: 0;
}

.report-item {
  --padding-start: 0;
  --inner-padding-end: 0;
  --background: transparent;
  margin-bottom: 12px;
}

.record-card {
  width: 100%;
  background: white;
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.03);
}

.record-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
}

.record-header h4 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: #333;
}

.record-details {
  display: flex;
  justify-content: space-between;
}

.detail-box {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.detail-box .label {
  font-size: 0.8rem;
  color: #888;
  margin-bottom: 4px;
}

.detail-box .time {
  font-size: 1.1rem;
  font-weight: 700;
  color: #333;
}

.text-danger {
  color: #eb445a !important;
}

.empty-state {
  text-align: center;
  padding: 30px;
  color: #888;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.03);
}

body.scanner-active {
  --background: transparent;
  --ion-background-color: transparent;
}
</style>
